import React, { useState } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import '../styles/AdvancedROIMetrics.css';

const AdvancedROIMetrics = ({ formData }) => {
  const { subscription } = useSubscription();
  const isPremium = subscription?.tier === 'PREMIUM' || subscription?.tier === 'UNLIMITED';
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isPremium) {
    return (
      <div className="advanced-roi-metrics">
        <div className="premium-upgrade-section">
          <div className="premium-badge">
            <span className="badge-icon">⭐</span>
            <span>Premium Feature</span>
          </div>
          <div className="upgrade-content">
            <h3>Advanced ROI Analysis</h3>
            <p>Unlock detailed property analytics, cash flow analysis, risk metrics, and investment insights.</p>
            <div className="feature-preview">
              <div className="feature-item">📊 Property Ratios & Metrics</div>
              <div className="feature-item">💰 Cash Flow Analysis</div>
              <div className="feature-item">🎯 Risk Assessment</div>
              <div className="feature-item">💡 Investment Insights</div>
            </div>
            <button className="upgrade-btn" onClick={() => window.location.href = '/subscription'}>
              Upgrade to Premium
            </button>
          </div>
        </div>
      </div>
    );
  }

  const calculateAdvancedMetrics = () => {
    const price = parseFloat(formData.propertyPrice) || 0;
    const monthlyRent = parseFloat(formData.monthlyRent) || 0;
    const downPaymentPercent = parseFloat(formData.downpaymentPercentage) / 100 || 1;
    const yearlyTax = parseFloat(formData.propertyTax) || 0;
    const yearlyInsurance = parseFloat(formData.yearlyInsurance) || 0;
    const annualHOA = (parseFloat(formData.monthlyHOA) || 0) * 12;
    const yearlyMaintenance = parseFloat(formData.yearlyMaintenance) || 0;
    const vacancyRate = parseFloat(formData.vacancyRate) / 100 || 0;
    const managementFee = parseFloat(formData.managementFeePercentage) / 100 || 0;
    const additionalExpenses = (parseFloat(formData.additionalExpenses) || 0) * 12;

    if (price === 0) return null;

    const annualRent = monthlyRent * 12;
    const effectiveRent = annualRent * (1 - vacancyRate);
    const totalOperatingExpenses = yearlyTax + yearlyInsurance + annualHOA + yearlyMaintenance + (effectiveRent * managementFee);
    const netOperatingIncome = effectiveRent - totalOperatingExpenses;
    const downPayment = price * downPaymentPercent;
    const loanAmount = price - downPayment;

    // Advanced Metrics
    const grossRentMultiplier = price / annualRent;
    const rentToPrice = (monthlyRent / price) * 100;
    const operatingExpenseRatio = (totalOperatingExpenses / effectiveRent) * 100;
    const debtServiceCoverageRatio = additionalExpenses > 0 ? netOperatingIncome / additionalExpenses : 'N/A';
    
    // Cash Flow Analysis
    const monthlyCashFlow = (netOperatingIncome - additionalExpenses) / 12;
    const annualCashFlow = netOperatingIncome - additionalExpenses;
    
    // Break-even Analysis
    const breakEvenOccupancy = ((totalOperatingExpenses + additionalExpenses) / annualRent) * 100;
    
    // Return Metrics
    const totalCashReturn = downPayment > 0 ? (annualCashFlow / downPayment) * 100 : 0;
    const returnOnInvestment = downPayment > 0 ? (annualCashFlow / downPayment) * 100 : 0;

    return {
      grossRentMultiplier: grossRentMultiplier.toFixed(2),
      rentToPrice: rentToPrice.toFixed(2),
      operatingExpenseRatio: operatingExpenseRatio.toFixed(1),
      debtServiceCoverageRatio: typeof debtServiceCoverageRatio === 'number' ? debtServiceCoverageRatio.toFixed(2) : debtServiceCoverageRatio,
      monthlyCashFlow: monthlyCashFlow.toFixed(0),
      annualCashFlow: annualCashFlow.toFixed(0),
      breakEvenOccupancy: breakEvenOccupancy.toFixed(1),
      totalCashReturn: totalCashReturn.toFixed(2),
      returnOnInvestment: returnOnInvestment.toFixed(2),
      effectiveRent: effectiveRent.toFixed(0),
      totalOperatingExpenses: totalOperatingExpenses.toFixed(0)
    };
  };

  const metrics = calculateAdvancedMetrics();

  if (!metrics) return null;

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    return num < 0 
      ? `-$${Math.abs(num).toLocaleString()}` 
      : `$${num.toLocaleString()}`;
  };

  const getCashFlowColor = (value) => {
    const num = parseFloat(value);
    if (num > 200) return 'positive';
    if (num > 0) return 'neutral';
    return 'negative';
  };

  return (
    <div className="advanced-roi-metrics">
      <div className="advanced-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <div className="premium-badge">
            <span className="badge-icon">⭐</span>
            <span>Premium Analytics</span>
          </div>
          <h3>Advanced ROI Analysis</h3>
        </div>
        <div className="expand-toggle">
          <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>
            {isExpanded ? '▼' : '▶'}
          </span>
          <span className="toggle-text">
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="advanced-content">
          <div className="metrics-grid">
            <div className="metric-category">
              <h4>Property Ratios</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Gross Rent Multiplier</span>
                  <span className="metric-value">{metrics.grossRentMultiplier}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Rent-to-Price Ratio</span>
                  <span className="metric-value">{metrics.rentToPrice}%</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Operating Expense Ratio</span>
                  <span className="metric-value">{metrics.operatingExpenseRatio}%</span>
                </div>
              </div>
            </div>

            <div className="metric-category">
              <h4>Cash Flow Analysis</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Monthly Cash Flow</span>
                  <span className={`metric-value ${getCashFlowColor(metrics.monthlyCashFlow)}`}>
                    {formatCurrency(metrics.monthlyCashFlow)}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Annual Cash Flow</span>
                  <span className={`metric-value ${getCashFlowColor(metrics.annualCashFlow)}`}>
                    {formatCurrency(metrics.annualCashFlow)}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Total Cash Return</span>
                  <span className="metric-value">{metrics.totalCashReturn}%</span>
                </div>
              </div>
            </div>

            <div className="metric-category">
              <h4>Risk Analysis</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Break-even Occupancy</span>
                  <span className="metric-value">{metrics.breakEvenOccupancy}%</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Debt Service Coverage</span>
                  <span className="metric-value">
                    {typeof metrics.debtServiceCoverageRatio === 'number' ? 
                      `${metrics.debtServiceCoverageRatio}x` : 
                      metrics.debtServiceCoverageRatio}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Return on Investment</span>
                  <span className="metric-value">{metrics.returnOnInvestment}%</span>
                </div>
              </div>
            </div>

            <div className="metric-category">
              <h4>Income & Expenses</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Effective Annual Rent</span>
                  <span className="metric-value">{formatCurrency(metrics.effectiveRent)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Total Operating Expenses</span>
                  <span className="metric-value">{formatCurrency(metrics.totalOperatingExpenses)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Net Operating Income</span>
                  <span className="metric-value">
                    {formatCurrency(parseFloat(metrics.effectiveRent) - parseFloat(metrics.totalOperatingExpenses))}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="insights-section">
            <h4>Key Insights</h4>
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-icon">📊</div>
                <div className="insight-content">
                  <h5>Cash Flow Status</h5>
                  <p>
                    {parseFloat(metrics.monthlyCashFlow) > 0 
                      ? `Positive cash flow of ${formatCurrency(metrics.monthlyCashFlow)}/month` 
                      : `Negative cash flow requiring ${formatCurrency(Math.abs(metrics.monthlyCashFlow))}/month`}
                  </p>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">🎯</div>
                <div className="insight-content">
                  <h5>Occupancy Risk</h5>
                  <p>
                    {parseFloat(metrics.breakEvenOccupancy) < 85 
                      ? "Low risk - comfortable occupancy buffer" 
                      : "Higher risk - requires high occupancy to break even"}
                  </p>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">💰</div>
                <div className="insight-content">
                  <h5>Investment Efficiency</h5>
                  <p>
                    {parseFloat(metrics.grossRentMultiplier) < 12 
                      ? "Good deal - rent covers costs well" 
                      : "Consider negotiating price or finding higher rent"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedROIMetrics;